import { CENTRAL_DATA_MANAGEMENT_MICROSERVICE } from "Constants";
import {
t,
translate,
} from "components/CentralDataMangement/translation/Translation";
import NoRecords from "components/common/atoms/NoRecords";
import PaginationNew from "components/common/atoms/PaginationNew";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import Title from "components/common/atoms/Title";
import { ApiCall } from "components/common/services/ApiServices";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CLARITALK_USER_LIST, CLARITALK_USER_SYNC } from "routes/ApiEndpoints";
import CustomNotify from "components/common/atoms/CustomNotify";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SearchFields from "components/common/atoms/SearchFields";
import ResetBtn from "components/common/atoms/ResetBtn";
import Sorting from "components/common/atoms/Sorting";
import { createClaritalkFilterConfig } from "./manage-salesnote/Filters";
import { formatDateAndTime } from "components/common/utlis/HelperFunctions";
import InfoIcon from "static/images/InfoIcon";

interface ClaritalkUserProps {
id: string;
name: string;
email: string;
createdAt: Date;
is_active: boolean;
}

interface SearchDataProps {
currentPage: number;
recordsPerPage: number;
name: string;
email: string;
status: any;
orderByColumn: string;
orderIn: "" | "asc" | "desc";
}

const ManageClaritalkUsers: React.FC = () => {
const [loading, setLoading] = useState(false);
const [syncing, setSyncing] = useState(false);
const [claritalkUsers, setClaritalkUsers] = useState<ClaritalkUserProps[]>([]);

// Pagination state
const [paginationData, setPaginationData] = useState({
    totalPages: 1,
    totalRecords: 0,
});

// Default search data
const defaultSearchData: SearchDataProps = {
    currentPage: 1,
    recordsPerPage: 10,
    name: "",
    email: "",
    status: null,
    orderByColumn: "",
    orderIn: "",
};

const [searchData, setSearchData] = useState<SearchDataProps>(defaultSearchData);

// Filter configuration
const filters = createClaritalkFilterConfig();

useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
    fetchClaritalkUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
}, [searchData]);

const fetchClaritalkUsers = async () => {
    setLoading(true);
    try {
    const data = {
        search: searchData,
    };

    const response = await ApiCall.service(
        CLARITALK_USER_LIST,
        "POST",
        data,
        false,
        CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
        setClaritalkUsers(response?.data || []);
        setPaginationData(
        response?.pagination || {
            totalPages: 1,
            totalRecords: 0,
        }
        );
    } else {
        CustomNotify({
        type: "error",
        message: t("Failed to fetch Claritalk users"),
        });
    }
    } catch (error) {
    CustomNotify({
        type: "error",
        message: t("Some error occurred"),
    });
    }
    setLoading(false);
};

const handleSyncUsers = async () => {
    setSyncing(true);
    try {
    const response = await ApiCall.service(
        CLARITALK_USER_SYNC,
        "POST",
        {},
        false,
        CENTRAL_DATA_MANAGEMENT_MICROSERVICE
    );

    if (response?.status === 200) {
        const syncData = response?.data;
        const message = response?.message || "Users synced successfully.";

        if (syncData?.synced > 0) {
        CustomNotify({
            type: "success",
            message: `${message}`,
        });
        } else if (syncData?.synced === 0) {
        CustomNotify({
            type: "success",
            message: "Everything is already synced.",
        });
        }

        // Refresh list and reset to page 1
        setSearchData((prev) => ({
        ...prev,
        currentPage: 1,
        }));
    } else {
        CustomNotify({
        type: "error",
        message: t("Failed to sync users"),
        });
    }
    } catch (error) {
    CustomNotify({
        type: "error",
        message: t("Sync failed. Please try again."),
    });
    }
    setSyncing(false);
};

const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setSearchData((prev) => ({
    ...prev,
    recordsPerPage: newItemsPerPage,
    currentPage: 1,
    }));
};

const handlePageChange = (pageNumber: number) => {
    setSearchData((prev) => ({
    ...prev,
    currentPage: pageNumber,
    }));
};

const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
) => {
    const { name, value } = e.target;
    setSearchData((prev) => ({
    ...prev,
    currentPage: 1,
    [name]: value,
    }));
};

const handleSelectChange = (selectedOption: any, fieldName: string) => {
    setSearchData((prev) => ({
    ...prev,
    currentPage: 1,
    [fieldName]: selectedOption,
    }));
};

const handleReset = () => {
    setSearchData(defaultSearchData);
};

const handleOrderByColumn = (column: string) => {
    let newOrder: "" | "asc" | "desc" = "";
    let newColumn = column;

    if (searchData.orderByColumn === column) {
    if (searchData.orderIn === "") {
        newOrder = "asc";
    } else if (searchData.orderIn === "asc") {
        newOrder = "desc";
    } else {
        newOrder = "";
        newColumn = "";
    }
    } else {
    newOrder = "asc";
    }

    setSearchData((prev) => ({
    ...prev,
    currentPage: 1,
    orderByColumn: newColumn,
    orderIn: newOrder,
    }));
};

return (
    <AccessControl
    requiredPermissions={[
        {
        permission: "Content creation",
        read: true,
        },
    ]}
    renderNoAccess={true}
    >
    <>
        <div className="main-container">
        <Title title={t("Claritalk users")} />
        <div className="position-relative tableMainWrapper">
            <div className="row">
            <div className="ManageCreateBtn">
                <AccessControl
                requiredPermissions={[
                    {
                    permission: "Content creation",
                    create: true,
                    },
                ]}
                >
                <button
                    className="form-button marginRightPoint5"
                    onClick={handleSyncUsers}
                    disabled={syncing || loading}
                >
                    <FontAwesomeIcon icon={faSync} spin={syncing} className="me-2" />
                    {syncing ? t("Syncing...") : t("Sync users")}
                </button>
                </AccessControl>
            </div>
            </div>

            <div className="table-responsive tableSection">
            <table className="table manageSyncUsers border-0 mb-0">
                <thead>
                <tr className="tableSearchFields">
                    <SearchFields
                    searchData={searchData}
                    filters={filters}
                    handleSearchChange={handleSearchChange}
                    handleSelectChange={handleSelectChange}
                    />
                    <th className="border-0">
                    <div className="d-flex align-items-center w-100">
                        <ResetBtn handleResetClick={handleReset} />
                    </div>
                    </th>
                </tr>
                </thead>
            </table>

            <div className="tableContainer manageSyncUsers scrollBarDesign">
                <table className="table table-hover">
                <thead>
                    <tr className="TableHeader">
                    <th
                        onClick={() => handleOrderByColumn("name")}
                        className="cursor-pointer"
                    >
                        {t("Name")}
                        <Sorting
                        sortType={
                            searchData.orderByColumn === "name"
                            ? searchData.orderIn
                            : ""
                        }
                        />
                    </th>
                    <th
                        onClick={() => handleOrderByColumn("email")}
                        className="cursor-pointer"
                    >
                        {t("Email")}
                        <Sorting
                        sortType={
                            searchData.orderByColumn === "email"
                            ? searchData.orderIn
                            : ""
                        }
                        />
                    </th>
                    <th>{t("Status")}</th>
                    <th>{t("Action")}</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                    <SpinnerWrapper headerLength={4} />
                    ) : (
                    <>
                        {claritalkUsers && claritalkUsers.length > 0 ? (
                        claritalkUsers.map((user) => {
                            const statusColor = user.is_active ? "bg-success" : "bg-danger";
                            const statusText = user.is_active ? t("Active") : t("Inactive");

                            return (
                            <tr key={user.id} className="border-bottom align-middle">
                                <td className="text-break" data-label={t("Name")}>{user.name}</td>
                                <td className="text-break" data-label={t("Email")}>{user.email}</td>
                                <td className="text-break" data-label={t("Status")}>
                                <span
                                    className={`d-inline-block rounded-circle status-icon ${statusColor}`}
                                    data-toggle="tooltip"
                                    title={statusText}
                                    style={{ marginLeft: "1vw" }}
                                ></span>
                                </td>
                                <td className="table-action-icons">
                                    <div className="d-inline-block">
                                        <div className="position-relative manageInfoIcon cursor-pointer">
                                            <InfoIcon />
                                            <span className="infoHelpText text-center">
                                                {t("Created at")}{" "}
                                                <span className="fw-bold">
                                                    {formatDateAndTime(user.createdAt)}
                                                </span>
                                            </span>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            );
                        })
                        ) : (
                        <NoRecords headerLength={4} />
                        )}
                    </>
                    )}
                </tbody>
                </table>
            </div>

            {!loading && (
                <div className="pagination d-block">
                <PaginationNew
                    currentPage={searchData.currentPage}
                    totalPages={paginationData.totalPages}
                    totalRecords={paginationData.totalRecords}
                    recordsPerPage={searchData.recordsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    onPageChange={(newPage: any) => handlePageChange(newPage)}
                />
                </div>
            )}
            </div>
        </div>
        </div>
        <div className="row backPadding">
        <div className="col-md-6 align-self-center">
            <Link to="/dashboard" className="back-btn text-decoration-underline">
            {t("Back")}
            </Link>
        </div>
        </div>
    </>
    </AccessControl>
);
};

export default translate(ManageClaritalkUsers);
